import {createApi} from '@reduxjs/toolkit/dist/query/react';

import {TLookupValue} from 'modules/data/data-types';
import {IAttachment} from 'shared/types/files';
import {ERequestType} from 'shared/types/requests';
import {axiosBaseQuery} from 'store/api/base-query';

export interface IReclaimingInformationAudit {
    id: string;
    statusCode: number;
    statusText: string;
    stage: TLookupValue;
    ticketId: string;
    createdDateMainDocument: string;
    lastModifiedDateMainDocument: string;
    fileList: IAttachment[];
    createdDate: string;
    lastModifiedDate: string;
}

export type TReclaimingInformationEntity = {
    id: string;
    mainDocNumber?: string;
    downloadDate?: string;
    fileListMainDocument?: IAttachment[];
    fileListOrderDocument?: IAttachment[];
    dateFromDecision?: string;
    docTypeName?: string;
    downloadingAudit?: IReclaimingInformationAudit;
    processingAudit?: IReclaimingInformationAudit;
    responseRequirementAudit?: IReclaimingInformationAudit;
    incomingProcessingAudit?: IReclaimingInformationAudit;
    notificationAudit?: IReclaimingInformationAudit;
    solutionAudit?: IReclaimingInformationAudit;
    audits?: IReclaimingInformationAudit[];
    protocolMainDocument?: IAttachment;
    responseRequirementDocument?: IAttachment;
    protocolResponseRequirementDocument?: IAttachment;
    notificationDocument?: IAttachment;
    solutionDocument?: IAttachment[];
};

interface IReclaimingApiCommonArgs {
    id: TReclaimingInformationEntity['id'];
}

export enum EExternalServiceNames {
    SUBMIT_PROTOCOL = 'submitProtocol',
    SUBMIT_RESPONSE_TO_DOC_REQUEST = 'submitResponseToDocRequest',
}

interface IRunProtocolServiceCommonArgs {
    externalServiceName: EExternalServiceNames;
    mainDocNumber?: string;
}

export const ReclaimingInformationApi = createApi({
    reducerPath: 'reclaimingInformationApi',
    baseQuery: axiosBaseQuery(),
    tagTypes: ['ReclaimingInformation'],
    endpoints: builder => ({
        getReclaimingInformation: builder.query<TReclaimingInformationEntity, IReclaimingApiCommonArgs>({
            query: ({id}) => ({
                url: `integration.module/reclaiming.information/${id}`,
            }),
            providesTags: ['ReclaimingInformation'],
        }),
        runSubmitProtocolService: builder.mutation<string, IRunProtocolServiceCommonArgs>({
            query: ({mainDocNumber, externalServiceName}) => {
                const formData = new FormData();

                if (mainDocNumber) formData.append('mainDocNumber', mainDocNumber);
                formData.append('externalServiceName', externalServiceName);
                formData.append('externalServiceVersion', 'v3');

                return ({
                    url: '/integration.module/monitor.services/execute',
                    method: ERequestType.POST,
                    data: formData,
                    formData: true,
                });
            },
        }),
        runSubmitInabilityInfoService: builder.mutation<void, IReclaimingApiCommonArgs>({
            query: ({id}) => ({
                url: '/v3/submitInabilityInfo',
                params: {inabilityInfoDocId: id},
                method: ERequestType.GET,
            }),
        }),
        runSubmitResponseToDocRequestService: builder.mutation<string, IRunProtocolServiceCommonArgs>({
            query: ({mainDocNumber, externalServiceName}) => {
                const formData = new FormData();

                if (mainDocNumber) formData.append('mainDocNumber', mainDocNumber);
                formData.append('externalServiceName', externalServiceName);
                formData.append('externalServiceVersion', 'v3');

                return {
                    url: '/integration.module/monitor.services/execute',
                    data: formData,
                    method: ERequestType.POST,
                    formData: true,
                };
            },
        }),
    }),
});

export const {
    useGetReclaimingInformationQuery,
    useLazyGetReclaimingInformationQuery,
    useRunSubmitProtocolServiceMutation,
    useRunSubmitInabilityInfoServiceMutation,
    useRunSubmitResponseToDocRequestServiceMutation,
} = ReclaimingInformationApi;
